// users.ts — the User Management screen (#/users).
//
// Split out of the old combined admin screen: users here, audit on its own
// page, each with its own nav entry. The list is AG Grid (the console's home
// grid pattern, palette from the appearance seat), and "Add user" is a
// separate modal rather than an inline form row.
//
// The grid shows what an admin actually needs to answer "who has access":
// the Google profile picture, the role, and when they were last active.
//
// The client hides what the server would refuse — it is never the thing
// deciding. Every action here re-reads the server's answer, and a refusal is
// rendered verbatim rather than being second-guessed into a generic message.

import type { ColDef, GridApi } from 'ag-grid-community';
import {
  ROLES, ROLE_BLURBS, ROLE_LABELS,
  avatarNode, el, jsonFetch, makeGrid, renderRefusal,
  rolePillHtml, timeCell, type Role,
} from './admin-ui.js';
import { adminGridMachinery } from './admin-grid.js';
import { gridRowsToCards, renderCardList, watchPhone } from './card-list.js';
import { ADMIN_GRID_TOOLBAR, renderGridToolbarButtons } from './grid-toolbar.js';
import { displayValueFromDefs } from './grid-machinery.js';
import { fmtWhen } from './home.js';
import { iconBtn, mkIconBtn, setBtnGlyph, setBtnLabel, setBtnState } from './buttons.js';
import { mkDropdown } from './dropdown.js';
import { mkFcSelect, mkFcText } from './form-controls.js';
import { mkSearchBox } from './search.js';
import type { CtxMenuItem } from './ctxmenu.js';
import {
  confirmDialog as dlgConfirm,
  destroyDialog,
  noticeDialog as dlgNotice,
  openDialog,
  showDialogMsg,
} from './dialog.js';
import { mountShell } from './shell.js';

export interface UserRow {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  disabled: boolean;
  bound: boolean;
  picture_url: string | null;
  last_active_at: string | null;
  last_login_at: string | null;
  invited_at: string | null;
  invite_accepted_at: string | null;
  invite_sent_via: string | null;
  created_at: string;
}

interface InviteResult {
  inviteUrl?: string;
  emailed?: boolean;
  emailReason?: string | null;
  error?: string;
}

/** Account state as ONE word, derived in one place so the grid, the filter
 * and the row styling cannot disagree. Exported for the headless test. */
export function accountState(u: UserRow): 'disabled' | 'invited' | 'active' {
  if (u.disabled) return 'disabled';
  if (!u.bound) return 'invited';
  return 'active';
}

// ---------------------------------------------------------------------
// R59a — the grid machinery's per-page seats (users-audit-parity).
// ---------------------------------------------------------------------

/** Canonical column order — what reconcileColumnState re-homes a stale
 * saved preset against. MUST match buildUserColumns's colIds (the headless
 * test pins both against each other). */
export const USERS_COLUMN_IDS = ['person', 'role', 'state', 'lastActive', 'actions'] as const;

/** No shipped pins (R59a decision, stated in the seam PR): this grid fits
 * its viewport with flex columns, so a default pin buys no keep-visible
 * benefit and costs a permanent split border. Pinning stays a user verb —
 * the seat's three states (absent/[]/list) are unchanged. */
export const DEFAULT_USERS_PINS: readonly string[] = [];

/** The eye's vocabulary for one cell (R59a): the filter's value list, Copy
 * and doesFilterPass all speak THIS. Two overrides where the machine-y
 * valueGetter differs from what the cell paints — the state pill's casing,
 * and the datetime column (a ticking "3m ago" is useless as a filter value;
 * fmtWhen is home.ts's exact trade for its own datetime columns). Every
 * other column rides the defs fallback automatically. Exported pure for the
 * headless test. */
export function usersDisplayValue(
  defs: ReadonlyArray<ColDef<UserRow>>,
  colId: string,
  row: UserRow,
): string {
  switch (colId) {
    case 'state': {
      const s = accountState(row);
      return s === 'invited' ? 'Invited' : s === 'disabled' ? 'Disabled' : 'Active';
    }
    case 'lastActive': {
      const s = fmtWhen(row.last_active_at);
      return s === '—' ? '' : s;
    }
    default:
      return displayValueFromDefs(defs, colId, row);
  }
}

// ---------------------------------------------------------------------
// bridge-ui-polish (owner items B1/B2/E) — the row-verb inventory, pure.
// ---------------------------------------------------------------------

export interface UserRowVerb {
  action: 'reinvite' | 'enable' | 'disable' | 'delete';
  /** The OLD text-button label — now the aria-label AND the data-tip
   * ("icon buttons that show a tool tip on hover with the button text"). */
  label: string;
  icon: string;
  danger?: boolean;
}

/** The actions CELL's inventory (item B1). The old Role button is NOT here
 * — item B2 moved that verb into the role field itself (the inline
 * dropdown); the context menu keeps a Change Role… entry for the menu
 * lane. Exported pure so the headless suite counts text-buttons ->
 * icon-buttons per state, by name. */
export function userRowVerbs(u: UserRow, meId: string | null): UserRowVerb[] {
  const out: UserRowVerb[] = [];
  if (!u.bound) out.push({ action: 'reinvite', label: 'Resend', icon: 'mail' });
  // OWNER RULING (Styles × Themes PR-3a, 2026-09-05): Disable IS destructive.
  // It destroys the person's sessions and cuts their live connections the
  // instant it is confirmed — the same class of act as Delete, differing only
  // in that the row survives. It rode the NEUTRAL ramp until now, which made
  // the one irreversible verb on this grid look like navigation. The row
  // context menu already said danger: true; the button did not, and the two
  // disagreeing about the same verb is the defect underneath the ruling.
  out.push(u.disabled
    ? { action: 'enable', label: 'Enable', icon: 'circle-check' }
    : { action: 'disable', label: 'Disable', icon: 'ban', danger: true });
  if (u.id !== meId) out.push({ action: 'delete', label: 'Delete', icon: 'trash-2', danger: true });
  return out;
}

export interface UserRowMenuEntry {
  id: 'reinvite' | 'role' | 'enable' | 'disable' | 'delete';
  label: string;
  icon: string;
  enabled: boolean;
  danger?: boolean;
  note?: string;
  tip?: string;
}

/** The row context menu's decision (item E): the page's verbs with their
 * WHY when disabled — the release grid's stable-roster doctrine (a verb
 * that vanishes reads as a bug; one that says why it is off reads as a
 * rule). The machinery appends the generic Copy Value after these. */
export function userRowMenuSpec(u: UserRow, meId: string | null): UserRowMenuEntry[] {
  return [
    {
      id: 'reinvite', label: 'Resend Invitation', icon: 'mail', enabled: !u.bound,
      ...(u.bound ? { note: 'accepted', tip: 'This account has already accepted its invitation' } : {}),
    },
    { id: 'role', label: 'Change Role…', icon: 'shield', enabled: true },
    u.disabled
      ? { id: 'enable', label: 'Enable', icon: 'circle-check', enabled: true }
      : { id: 'disable', label: 'Disable', icon: 'ban', enabled: true, danger: true },
    {
      id: 'delete', label: 'Delete', icon: 'trash-2', enabled: u.id !== meId, danger: true,
      ...(u.id === meId ? { tip: 'You cannot delete your own account' } : {}),
    },
  ];
}

export function buildUserColumns(ctx: {
  meId: string | null;
  onAction: (action: string, row: UserRow) => void;
  /** Item B2 — the role cell's inline commit. Optional so older callers
   * (and the headless suite's cheap drills) can render without wiring. */
  onRolePick?: (row: UserRow, role: Role) => void;
}): ColDef<UserRow>[] {
  return [
    {
      colId: 'person', headerName: 'User', flex: 2, minWidth: 240, sort: 'asc',
      valueGetter: (p) => p.data?.name ?? p.data?.email ?? '',
      cellRenderer: (p: { data?: UserRow }) => {
        const wrap = el('span', 'hc-person');
        if (!p.data) return wrap;
        wrap.appendChild(avatarNode(p.data.picture_url, p.data.name, p.data.email));
        const text = el('span', 'hc-person-text');
        const line1 = el('span', 'hc-person-name', p.data.name ?? p.data.email);
        if (p.data.id === ctx.meId) line1.appendChild(el('span', 'hc-you', 'you'));
        text.appendChild(line1);
        text.appendChild(el('span', 'hc-person-mail', p.data.email));
        wrap.appendChild(text);
        return wrap;
      },
    },
    {
      colId: 'role', headerName: 'Role', flex: 1, minWidth: 170,
      valueGetter: (p) => (p.data ? ROLE_LABELS[p.data.role] ?? p.data.role : ''),
      // Item B2: the role FIELD is the control — an inline themed dropdown
      // (dropdown.ts, portal lane: AG cells clip and AG's transformed rows
      // break fixed descendants). Closed face = the role pill; open rows =
      // role name over its subtitle, THE dialog's exact copy — one source
      // of truth (ROLE_LABELS/ROLE_BLURBS), never a second string.
      cellRenderer: (p: { data?: UserRow }) => {
        if (!p.data) return document.createElement('span');
        const u = p.data;
        const dd = mkDropdown({
          id: `hcRole-${u.id}`,
          portal: true,
          options: () => ROLES.map((r) => ({
            value: r,
            label: ROLE_LABELS[r]!,
            labelHtml: rolePillHtml(r),
            html: `<span class="hc-dd-roleopt"><span class="hc-dd-title">${ROLE_LABELS[r]!}</span>`
              + `<span class="hc-dd-sub">${ROLE_BLURBS[r]!}</span></span>`,
          })),
          value: () => u.role,
          onPick: (v) => ctx.onRolePick?.(u, v as Role),
        });
        dd.el.classList.add('hc-role-dd');
        const btn = dd.el.querySelector<HTMLElement>('.hc-dd-btn');
        btn?.setAttribute('aria-label', `Change role — currently ${ROLE_LABELS[u.role] ?? u.role}`);
        return dd.el;
      },
    },
    {
      colId: 'state', headerName: 'Status', flex: 1, minWidth: 120,
      valueGetter: (p) => (p.data ? accountState(p.data) : ''),
      cellRenderer: (p: { data?: UserRow }) => {
        const e = document.createElement('span');
        if (!p.data) return e;
        const state = accountState(p.data);
        e.className = `hc-pill hc-pill-state-${state}`;
        e.textContent = state === 'invited' ? 'Invited' : state === 'disabled' ? 'Disabled' : 'Active';
        if (state === 'invited' && p.data.invite_sent_via) {
          e.dataset.tip = p.data.invite_sent_via === 'email'
            ? 'Invitation emailed — not yet accepted'
            : 'Invitation link issued (email not configured) — not yet accepted';
        }
        return e;
      },
    },
    {
      colId: 'lastActive', headerName: 'Last active', flex: 1, minWidth: 140,
      // Sort on the raw instant, render the human form.
      comparator: (_a, _b, na, nb) => {
        const ta = na.data?.last_active_at ? Date.parse(na.data.last_active_at) : 0;
        const tb = nb.data?.last_active_at ? Date.parse(nb.data.last_active_at) : 0;
        return ta - tb;
      },
      valueGetter: (p) => p.data?.last_active_at ?? '',
      cellRenderer: (p: { data?: UserRow }) => timeCell(p.data?.last_active_at ?? null),
    },
    {
      colId: 'actions', headerName: '', flex: 1, minWidth: 130, sortable: false,
      // Item B1: icon buttons with the OLD text as aria-label and data-tip
      // — the tooltip is the shared layer's, never the accessible name and
      // never native title. The inventory is userRowVerbs (pure, counted
      // by the headless suite); the Role text button is GONE from here —
      // the role cell's dropdown is that verb now (B2).
      cellRenderer: (p: { data?: UserRow }) => {
        const wrap = el('span', 'hc-actions');
        if (!p.data) return wrap;
        const u = p.data;
        for (const v of userRowVerbs(u, ctx.meId)) {
          // Styles × Themes PR-2: the row verbs ride the primitive —
          // hc-rowbtn / hc-rowbtn-danger's hand-picked ghost and danger
          // paint became the neutral / destructive ramp, and hc-rowact
          // carries only the inter-button gap.
          const b = mkIconBtn({
            glyph: v.icon, label: v.label, size: 'sm', cls: 'hc-rowact',
            // A danger verb's click OPENS a confirmation dialog that asks
            // with the consequence (deleteDialog for Delete, dlgConfirm for
            // Disable — "their sessions are destroyed and any live
            // connections are cut immediately"), so arming here too would be
            // a double confirm. The reason is rendered into the DOM as
            // data-btn-noconfirm, so it is visible in the page.
            ...(v.danger
              ? {
                  intent: 'destructive' as const,
                  noConfirm: 'the click opens this verb’s confirmation dialog, which asks with the consequence',
                }
              : {}),
          });
          b.addEventListener('click', () => ctx.onAction(v.action, u));
          wrap.appendChild(b);
        }
        return wrap;
      },
    },
  ];
}

export function mountUsers(root: HTMLElement): () => void {
  // The ONE shell (shared-shell, R60/R63): same brand, nav, appearance
  // control and account circle as the home screen. This page's own state —
  // its title and the "12 users" count — lives in the pagebar row below the
  // top strip (R68), and the returned teardown is the shell's: the router
  // must call it, or the shell's document-level controls leak per visit.
  const shell = mountShell(root, 'users');
  shell.pagebar.innerHTML = `
    <h1 class="hc-pagetitle">User management</h1>
    <div class="hc-status" id="hcAdminStatus"></div>`;
  shell.main.classList.add('hc-admin');
  shell.main.innerHTML = `
    <div class="hc-toolbar" id="hcToolbar"></div>
    <div id="hcGrid" class="hc-grid"></div>
    <div id="hcCardList" class="hc-cardlist"></div>`;
  const status = root.querySelector('#hcAdminStatus') as HTMLElement;
  const toolbar = root.querySelector('#hcToolbar') as HTMLElement;
  const cardListEl = root.querySelector('#hcCardList') as HTMLElement;
  const modal = shell.modal;

  let grid: GridApi<UserRow> | null = null;
  let rows: UserRow[] = [];
  let meId: string | null = null;
  /** The defs the live grid was built with — the picker's row source and
   * the display vocabulary's defs fallback (home.ts's currentDefs shape). */
  let currentDefs: ColDef<UserRow>[] = [];
  let machTeardown: (() => void) | null = null;

  // R59a — the release grid's machinery on THIS page's seat: filters, pins
  // and Copy over whatever ColDefs exist, persisted in ui_state
  // grid-preset/'users' (the EXISTING keyed kind — no server change).
  const mach = adminGridMachinery<UserRow>({
    presetKey: 'users',
    defaultPins: DEFAULT_USERS_PINS,
    unfiltered: ['actions'],
    columnIds: USERS_COLUMN_IDS,
    displayValue: (c, r) => usersDisplayValue(currentDefs, c, r),
    rows: () => rows,
    // Item E — the page's verbs above the machinery's generic Copy Value.
    // ONE dispatch: every entry runs the same lanes the cell buttons and
    // the role dropdown take (onAction / roleDialog), never a second copy.
    rowMenu: (u) => userRowMenuSpec(u, meId).map((s): CtxMenuItem => ({
      label: s.label,
      icon: s.icon,
      disabled: !s.enabled,
      ...(s.note ? { note: s.note } : {}),
      ...(s.tip ? { tip: s.tip } : {}),
      ...(s.danger ? { danger: true } : {}),
      run: () => { s.id === 'role' ? roleDialog(u) : void onAction(s.id, u); },
    })),
  });

  // R59b — Columns/Reset/Refresh from THE grid-toolbar roster land in the
  // page's existing toolbar row; 'Add user' STAYS the page's primary action
  // (ruling Q15 default), rightmost like home's #hcCreate — now icon-only
  // (item B3: user-plus glyph, the text on hover via data-tip, the
  // accessible name unchanged). The search is the shared component (item
  // D), leftmost above the grid; the INPUT keeps id hcUserSearch.
  toolbar.innerHTML = `
    <span class="hc-spacer"></span>
    ${renderGridToolbarButtons({
      include: ADMIN_GRID_TOOLBAR,
      tips: { hcRefresh: 'Refresh the user list now' },
    })}
    ${iconBtn({
      // THE additive action of this page (ruling Q15 default: Users keeps
      // its own primary; Create stays home's). hc-gtbtn stays as the
      // toolbar geometry the roster buttons beside it wear; hc-gtbtn-primary
      // is retired — the additive ramp is the signifier now.
      glyph: 'user-plus', label: 'Add user', intent: 'additive', ctx: 'users-page',
      size: 'sm', id: 'hcAddUser', cls: 'hc-gtbtn',
    })}`;
  const searchBox = mkSearchBox({
    id: 'hcUserSearch',
    placeholder: 'Search name, email or role',
    label: 'Search users',
    onInput: () => applyFilter(),
  });
  toolbar.prepend(searchBox.el);
  const search = searchBox.input;
  (toolbar.querySelector('#hcAddUser') as HTMLElement)
    .addEventListener('click', () => inviteDialog());
  // R32's console grammar: the spin rides the in-flight window; a click
  // while spinning is refused — the spin IS the acknowledgment.
  const refreshBtn = toolbar.querySelector('#hcRefresh') as HTMLButtonElement;
  refreshBtn.addEventListener('click', () => {
    if (refreshBtn.classList.contains('hc-spinning')) return;
    refreshBtn.classList.add('hc-spinning');
    void load().finally(() => refreshBtn.classList.remove('hc-spinning'));
  });

  function applyFilter(): void {
    const q = search.value.trim().toLowerCase();
    const shown = !q ? rows : rows.filter((u) =>
      [u.email, u.name ?? '', ROLE_LABELS[u.role] ?? u.role, accountState(u)]
        .join(' ').toLowerCase().includes(q));
    grid?.setGridOption('rowData', shown);
    // The phone card column (owner ruling 2026-09-06): each user row as a
    // stacked key:value card. Built only on a phone; the display vocabulary
    // is the SAME one the grid's filter/copy speak (usersDisplayValue).
    if (phone.isPhone()) {
      renderCardList(cardListEl, gridRowsToCards(shown, {
        columnIds: USERS_COLUMN_IDS,
        headerName: (c) => currentDefs.find((d) => d.colId === c)?.headerName ?? c,
        displayValue: (c, r) => usersDisplayValue(currentDefs, c, r),
        titleCol: 'person',
        skipCols: ['actions'],
      }), { empty: q ? 'No users match your search.' : 'No users yet.' });
    } else {
      cardListEl.replaceChildren();
    }
    status.textContent = q
      ? `${shown.length} of ${rows.length} users`
      : `${rows.length} user${rows.length === 1 ? '' : 's'}`;
  }
  const phone = watchPhone(() => applyFilter());

  async function load(): Promise<void> {
    const me = await jsonFetch<{ id: string }>('GET', '/api/me');
    meId = me.data?.id ?? null;
    const { status: st, data } = await jsonFetch<{ users: UserRow[] }>('GET', '/api/admin/users');
    if (st === 401 || st === 403) return renderRefusal(root, st);
    rows = data?.users ?? [];
    if (!grid) {
      const gridEl = root.querySelector('#hcGrid') as HTMLElement;
      currentDefs = buildUserColumns({
        meId,
        onAction: (a, r) => void onAction(a, r),
        onRolePick: (u, role) => commitRole(u, role),
      });
      grid = makeGrid<UserRow>(gridEl, {
        // The one decoration pass (R59a) — filters + pins over the defs.
        columnDefs: mach.decorate(currentDefs),
        rowData: rows,
        getRowClass: (p) => (p.data && accountState(p.data) === 'disabled' ? 'hc-row-disabled' : ''),
        // Column changes persist into the SAME seat the filters ride.
        onColumnMoved: () => mach.persist(),
        onColumnResized: () => mach.persist(),
        onColumnVisible: () => mach.persist(),
      });
      void mach.wire(grid, {
        gridEl,
        colsBtn: toolbar.querySelector('#hcColsBtn'),
        colsMenu: toolbar.querySelector('#hcColsMenu'),
        resetBtn: toolbar.querySelector('#hcReset'),
      }).then((td) => { machTeardown = td; });
    }
    applyFilter();
  }

  // -------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------

  /** Item B2 — the role dropdown's commit lane. The row object mutates
   * FIRST (the closed face paints the pick before the server answers —
   * mkDropdown repaints from value() on pick), the PATCH rides behind, a
   * refusal reverts and renders VERBATIM, and load() re-reads the server's
   * truth either way. Same endpoint the old dialog hit — one contract. */
  function commitRole(u: UserRow, role: Role): void {
    const prev = u.role;
    if (role === prev) return;
    u.role = role;
    void jsonFetch<{ error?: string }>('PATCH', `/api/admin/users/${u.id}`, { role })
      .then((r) => {
        if (r.status !== 200) {
          u.role = prev;
          notice('Could not change role', r.data?.error ?? `Refused (${r.status})`);
        }
        return load();
      });
  }

  async function onAction(action: string, u: UserRow): Promise<void> {
    if (action === 'role') return roleDialog(u);
    if (action === 'delete') return deleteDialog(u);
    if (action === 'reinvite') {
      const r = await jsonFetch<InviteResult>('POST', `/api/admin/users/${u.id}/reinvite`);
      if (r.status !== 200) return notice('Could not resend', r.data?.error ?? `Refused (${r.status})`);
      showInviteOutcome(u.email, r.data ?? {});
      return load();
    }
    // disable / enable
    if (action === 'disable') {
      // Destructive: it cuts live connections NOW — the primary carries the
      // danger emphasis (the old confirmThen rendered it as a plain accent).
      dlgConfirm(modal, {
        title: `Disable ${u.email}?`,
        body: 'Their sessions are destroyed and any live connections are cut immediately.',
        confirmLabel: 'Disable',
        danger: true,
        onConfirm: () => {
          void (async () => {
            const r = await jsonFetch<{ sessionsDestroyed?: number; socketsSevered?: number; error?: string }>(
              'POST', `/api/admin/users/${u.id}/disable`);
            if (r.status !== 200) return notice('Could not disable', r.data?.error ?? `Refused (${r.status})`);
            notice('User disabled',
              `${r.data?.sessionsDestroyed ?? 0} session(s) destroyed, ${r.data?.socketsSevered ?? 0} live connection(s) cut.`);
            return load();
          })();
        },
      });
      return;
    }
    const r = await jsonFetch<{ error?: string }>('POST', `/api/admin/users/${u.id}/enable`);
    if (r.status !== 200) return notice('Could not enable', r.data?.error ?? `Refused (${r.status})`);
    return load();
  }

  function roleDialog(u: UserRow): void {
    openDialog(modal, (body, close) => {
      body.appendChild(el('h2', 'hc-dialog-title', 'Change role'));
      body.appendChild(el('p', 'hc-status', u.email));
      const list = el('div', 'hc-rolelist');
      let chosen: Role = u.role;
      for (const r of ROLES) {
        const opt = el('label', 'hc-roleopt');
        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = 'role';
        radio.value = r;
        radio.checked = r === u.role;
        radio.addEventListener('change', () => { chosen = r; });
        opt.appendChild(radio);
        const txt = el('span');
        txt.appendChild(el('span', 'hc-roleopt-name', ROLE_LABELS[r]!));
        txt.appendChild(el('span', 'hc-roleopt-blurb', ROLE_BLURBS[r]!));
        opt.appendChild(txt);
        list.appendChild(opt);
      }
      body.appendChild(list);
      const msg = el('p', 'hc-dialog-msg');
      body.appendChild(msg);
      const actions = el('div', 'hc-dialog-actions');
      // THE additive action of THIS dialog (per-dialog ctx: two stacked
      // dialogs never share a view context). The busy face is the
      // primitive's derivation — hc-btn-busy was the .hc-btn spinner.
      const save = mkIconBtn({
        glyph: 'check', label: 'Save', intent: 'additive', ctx: 'change-role-dialog',
      });
      save.addEventListener('click', () => {
        setBtnState(save, 'busy');
        void jsonFetch<{ error?: string; socketsSevered?: number }>(
          'PATCH', `/api/admin/users/${u.id}`, { role: chosen }).then((r) => {
          setBtnState(save, 'enabled');
          if (r.status !== 200) { showDialogMsg(msg, r.data?.error ?? `Refused (${r.status})`); return; }
          close();
          void load();
        });
      });
      const cancel = mkIconBtn({ glyph: 'x', label: 'Cancel' });
      cancel.addEventListener('click', close);
      actions.appendChild(cancel);
      actions.appendChild(save);
      body.appendChild(actions);
    });
  }

  function deleteDialog(u: UserRow): void {
    dlgConfirm(modal, {
      title: `Delete ${u.email}?`,
      body: 'They are removed from the sign-in allowlist, their sessions are revoked, and any live connections are cut. ' +
        'If they own releases or uploaded files their account is disabled instead, so that history survives.',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: () => {
        void (async () => {
          const r = await jsonFetch<{ error?: string; sessionsDestroyed?: number; disabled?: boolean }>(
            'DELETE', `/api/admin/users/${u.id}`);
          if (r.status === 409) return notice('Account disabled instead', r.data?.error ?? '');
          if (r.status !== 200) return notice('Could not delete', r.data?.error ?? `Refused (${r.status})`);
          notice('User deleted', `${r.data?.sessionsDestroyed ?? 0} session(s) revoked.`);
          return load();
        })();
      },
    });
  }

  /** R62 — the Add User modal on the engine: title/body/field hierarchy,
   * Cancel left of the primary, busy state while the POST is in flight, and
   * the refusal message ARRIVES (animated entrance) rather than editing
   * itself into place. */
  function inviteDialog(): void {
    openDialog(modal, (body, close) => {
      body.appendChild(el('h2', 'hc-dialog-title', 'Add user'));
      body.appendChild(el('p', 'hc-dialog-body',
        'They will be able to sign in with this Google address once invited.'));
      const form = document.createElement('form');
      form.className = 'hc-form';

      const emailLabel = el('label', 'hc-field');
      emailLabel.appendChild(el('span', 'hc-field-label', 'Email address'));
      // The form-control primitive (#1658 slice b): its <input> is type=text
      // by construction, so the email type goes on AFTER — the field stays a
      // native email control for `required` validation.
      const email = mkFcText({ label: 'Email address', placeholder: 'person@company.com' });
      email.input.type = 'email';
      email.input.name = 'email';
      email.input.required = true;
      email.input.setAttribute('data-autofocus', '');
      emailLabel.appendChild(email.el);
      form.appendChild(emailLabel);

      const roleLabel = el('label', 'hc-field');
      roleLabel.appendChild(el('span', 'hc-field-label', 'Role'));
      // The invite dialog's role picker was the LAST native <select> in this
      // file — which also used mkDropdown for the grid-cell role editor two
      // hundred lines up. One file, two answers to the same question, and
      // only one of them themeable. It is the primitive now.
      const blurb = el('p', 'hc-roleblurb', ROLE_BLURBS.member!);
      const select = mkFcSelect({
        label: 'Role',
        options: ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r]! })),
        value: 'member',
        onPick: (v) => { blurb.textContent = ROLE_BLURBS[v] ?? ''; },
      });
      roleLabel.appendChild(select.el);
      form.appendChild(roleLabel);
      form.appendChild(blurb);

      const msg = el('p', 'hc-dialog-msg');
      form.appendChild(msg);

      const actions = el('div', 'hc-dialog-actions');
      // `type: 'submit'` rides the spec, not attrs: the primitive writes
      // `type` itself and a duplicate attribute silently loses.
      const submit = mkIconBtn({
        glyph: 'send', label: 'Send invitation', intent: 'additive',
        ctx: 'add-user-dialog', type: 'submit',
      });
      const cancel = mkIconBtn({ glyph: 'x', label: 'Cancel' });
      cancel.addEventListener('click', close);
      actions.appendChild(cancel);
      actions.appendChild(submit);
      form.appendChild(actions);

      form.addEventListener('submit', (ev) => {
        ev.preventDefault();
        msg.textContent = '';
        setBtnState(submit, 'busy');
        void jsonFetch<InviteResult>('POST', '/api/admin/users', {
          email: email.value.trim(), role: select.value,
        }).then((r) => {
          setBtnState(submit, 'enabled');
          if (r.status !== 201) {
            showDialogMsg(msg, r.data?.error ?? `Refused (${r.status})`);
            return;
          }
          close();
          showInviteOutcome(email.value.trim(), r.data ?? {});
          void load();
        });
      });
      body.appendChild(form);
    });
  }

  /** The invite outcome dialog. When the email seat is absent this is the ONLY
   * way the invitee gets their link, so the link is selectable, copyable, and
   * the reason the email did not go is stated loudly — never a silent
   * "invited" that nobody ever receives. */
  function showInviteOutcome(email: string, r: InviteResult): void {
    openDialog(modal, (body, close) => {
      body.appendChild(el('h2', 'hc-dialog-title', r.emailed ? 'Invitation sent' : 'Invitation created'));
      body.appendChild(el('p', 'hc-dialog-body', r.emailed
        ? `An invitation email is on its way to ${email}.`
        : `${email} has been added. Send them the link below to finish.`));

      if (!r.emailed) {
        const warn = el('div', 'hc-warn');
        warn.appendChild(el('strong', '', 'Email not configured — no message was sent.'));
        if (r.emailReason) warn.appendChild(el('span', 'hc-warn-detail', r.emailReason));
        body.appendChild(warn);
      }

      if (r.inviteUrl) {
        const row = el('div', 'hc-copyrow');
        // `.hc-copyfield` rides the primitive's input through the skin seat,
        // BESIDE the fc-* classes (#1658 slice b).
        const field = mkFcText({ label: 'Invite link', value: r.inviteUrl, skin: { input: 'hc-copyfield' } });
        const input = field.input;
        input.readOnly = true;
        input.addEventListener('focus', () => input.select());
        const copy = mkIconBtn({ glyph: 'copy', label: 'Copy' });
        // Icon-only: the outcome rides the GLYPH and the label (one string,
        // tooltip and accessible name together). A textContent write would
        // erase the sprite the button IS.
        const settle = (glyph: string, label: string): void => {
          setBtnGlyph(copy, glyph);
          setBtnLabel(copy, label);
          window.setTimeout(() => { setBtnGlyph(copy, 'copy'); setBtnLabel(copy, 'Copy'); }, 1600);
        };
        copy.addEventListener('click', () => {
          input.select();
          void navigator.clipboard?.writeText(input.value).then(
            () => settle('check', 'Copied'),
            () => settle('circle-alert', 'Press ⌘C'),
          );
        });
        row.appendChild(field.el);
        row.appendChild(copy);
        body.appendChild(row);
      }

      const actions = el('div', 'hc-dialog-actions');
      // Acknowledgment, not creation — neutral (noticeDialog's precedent).
      const done = mkIconBtn({ glyph: 'check', label: 'Done', attrs: { 'data-act': 'ok' } });
      done.addEventListener('click', close);
      actions.appendChild(done);
      body.appendChild(actions);
    });
  }

  // confirmThen is DELETED: the two confirmations above call the engine's
  // confirmDialog directly (same leaves-now semantics — close, then the
  // work rides behind — plus the trap, both animated edges, and the R5
  // layout the hand-rolled version lacked).

  function notice(title: string, detail: string): void {
    dlgNotice(modal, { title, body: detail });
  }

  void load();
  // A dialog open across a route change would leak the engine's document
  // keydown listener — tear it down instantly before the shell goes. The
  // machinery's teardown joins it: the column picker's document click
  // dismiss would otherwise accumulate one copy per navigation.
  return () => {
    phone.teardown();
    machTeardown?.();
    destroyDialog(modal);
    shell.teardown();
  };
}
